import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useTranslation } from 'react-i18next';
import { useSalesDocument, useNextSalesNumber } from '@/hooks/use-sales';
import { useClients } from '@/hooks/use-clients';
import { CreditNoteForm } from './CreditNoteForm';
import { useArticles } from '@/hooks/use-articles';
import { useCompanySettings } from '@/hooks/use-settings';
import { usePdfTemplates } from '@/hooks/use-pdf-templates';
import { activeTemplatesFor, companyInfoForPdf, printWithTemplate } from '@/pages/settings/print-with-template';
import { SalesDocumentForm } from './SalesDocumentForm';
import { LinkedDocuments } from './LinkedDocuments';
import { resolveDocumentCustomer } from './document-customer';
import { CreateCertificateButton } from '@/pages/withholding/CreateCertificateButton';
import type { SalesDocType, SalesDocument, Article } from '@erp/data-access/interfaces';
import { Badge, Button, Card, CardContent, PageHeader, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@erp/ui';
import { Printer, AlertTriangle, ChevronDown } from '@/components/icons';

interface SalesDocumentPageProps {
  docType: SalesDocType;
  basePath: string;
}

export function SalesDocumentPage({ docType, basePath }: SalesDocumentPageProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useSmartNavigate();
  const isNew = id === 'new';
  const fromId = searchParams.get('from');
  const returnTo = searchParams.get('returnTo');
  const afterSavePath = returnTo && returnTo.startsWith('/') ? returnTo : basePath;

  const {
    data: document,
    isLoading,
    isPlaceholderData: isDocumentPlaceholder,
  } = useSalesDocument(isNew ? '' : (id ?? ''));
  const { data: nextNumber } = useNextSalesNumber(docType);
  const {
    data: sourceDoc,
    isLoading: sourceLoading,
    isPlaceholderData: isSourceDocPlaceholder,
  } = useSalesDocument(fromId ?? '');
  const { data: clients } = useClients();
  const { data: articles } = useArticles({ limit: 1000 });
  const { data: companySettings } = useCompanySettings();
  const { data: pdfTemplates } = usePdfTemplates();

  // Track whether user wants to keep non-invoiceable articles
  const [keepNonInvoiceable, setKeepNonInvoiceable] = useState(false);

  const articleMap = useMemo(() => {
    const map = new Map<string, Article>();
    articles?.forEach((a) => map.set(a.id, a));
    return map;
  }, [articles]);

  const activeTemplates = useMemo(
    () => document ? activeTemplatesFor(pdfTemplates, 'sales', document.docType) : [],
    [pdfTemplates, document],
  );

  const docTypeLabel = t(`sales.${docType === 'credit_note' ? 'creditNote' : docType}`);

  // The sales list cache intentionally stores lightweight rows without
  // sales_document_lines. Do not mount the edit/transform form from that
  // placeholder, because react-hook-form would keep empty default lines even
  // after the full document arrives, and a save would erase the real lines.
  if (
    (!isNew && ((isLoading && !document) || isDocumentPlaceholder)) ||
    (fromId && ((sourceLoading && !sourceDoc) || isSourceDocPlaceholder))
  ) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }

  // Build initialDoc — filter non-invoiceable articles when creating invoice from BL
  let initialDoc: SalesDocument | null = null;
  let nonInvoiceableLines: string[] = [];

  if (isNew && sourceDoc) {
    const isTransformToInvoice = docType === 'invoice';
    let filteredLines = sourceDoc.lines ?? [];

    if (isTransformToInvoice && !keepNonInvoiceable) {
      nonInvoiceableLines = filteredLines
        .filter((l) => l.articleId && !articleMap.get(l.articleId)?.isInvoiceable)
        .map((l) => l.description);

      if (nonInvoiceableLines.length > 0) {
        filteredLines = filteredLines.filter((l) => {
          if (!l.articleId) return true; // free entries always kept
          return articleMap.get(l.articleId)?.isInvoiceable !== false;
        });
      }
    }

    initialDoc = {
      ...sourceDoc,
      id: '',
      docType,
      number: '',
      parentDocId: sourceDoc.id,
      parentDocIds: [sourceDoc.id],
      lines: filteredLines,
    };
  } else if (!isNew) {
    initialDoc = document ?? null;
  }

  const buildPrintPayload = () => {
    if (!document) return null;
    const documentCustomer = resolveDocumentCustomer(document, clients) ?? {
      code: document.clientId || null,
      name: document.clientId || 'Client',
      type: 'individual' as const,
      address: null,
      city: null,
      phone: null,
    };
    return {
      company: companyInfoForPdf(companySettings),
      data: {
      docType: document.docType,
      number: document.number,
      docDate: document.docDate,
      validityDate: document.validityDate,
      client: {
        code: documentCustomer.code ?? '', name: documentCustomer.name, type: documentCustomer.type,
        taxId: null,
        address: documentCustomer.address ?? null,
        city: documentCustomer.city ?? null,
        phone: documentCustomer.phone ?? null,
        fax: null,
        carRegistrationNumber: document.carRegistrationNumber ?? null,
      },
      lines: (document.lines ?? []).map((l, i) => ({
        lineNumber: i + 1, description: l.description, quantity: l.quantity,
        unitPriceHt: l.unitPriceHt, discountPct: l.discountPct, vatRate: l.vatRate, totalHt: l.totalHt,
        articleCode: articles?.find((a) => a.id === l.articleId)?.code ?? null,
      })),
      totalHt: document.totalHt,
      totalDiscount: document.totalDiscount,
      totalVat: document.totalVat,
      totalTtc: document.totalTtc,
      withholdingAmount: document.withholdingAmount,
      stampAmount: document.stampAmount,
      netToPay: document.netToPay,
      notes: document.notes,
      },
    };
  };

  const runPrint = (tpl: typeof activeTemplates[number]) => {
    const payload = buildPrintPayload();
    if (!document || !payload) return;
    printWithTemplate(tpl, payload.company, payload.data, 'sales', document.docType);
  };

  const headerActions = !isNew && document ? (
    <>
      {docType === 'invoice' && (() => {
        const client = clients?.find((c) => c.id === document.clientId);
        if (!client) return null;
        return (
          <CreateCertificateButton
            certificateType="sale"
            sourceDocument={{
              id: document.id,
              number: document.number,
              docDate: document.docDate,
              totalHt: document.totalHt,
              totalVat: document.totalVat,
              totalTtc: document.totalTtc,
            }}
            thirdParty={{ id: client.id, type: client.type }}
          />
        );
      })()}
      {activeTemplates.length <= 1 ? (
        <Button
          type="button"
          variant="outline"
          aria-label={t('sales.printPdf')}
          onClick={() => {
            const tpl = activeTemplates[0];
            if (tpl) runPrint(tpl);
          }}
        >
          <Printer size={14} className="sm:me-1.5" />
          <span className="hidden sm:inline">{t('sales.printPdf')}</span>
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" aria-label={t('sales.printPdf')}>
              <Printer size={14} className="sm:me-1.5" />
              <span className="hidden sm:inline">{t('sales.printPdf')}</span>
              <ChevronDown size={12} className="ms-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {activeTemplates.map((tpl) => (
              <DropdownMenuItem key={tpl.id} onClick={() => runPrint(tpl)}>
                <Printer size={14} />
                <span className="flex-1 truncate">{tpl.name}</span>
                {tpl.isDefault && <Badge variant="outline" className="ms-2 text-[10px]">{t('pdfTemplates.default')}</Badge>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-3 min-w-0">
            <span className="truncate">
              {isNew
                ? t('sales.newDocument', { type: docTypeLabel })
                : t('sales.editDocument', { type: docTypeLabel })}
            </span>
            {!isNew && document && (
              <Badge variant="outline" className="font-mono text-sm shrink-0">
                {document.number}
              </Badge>
            )}
            {isNew && nextNumber && (
              <Badge variant="outline" className="font-mono text-sm shrink-0 border-dashed text-muted-foreground">
                {nextNumber}
              </Badge>
            )}
          </span>
        }
        actions={headerActions}
      />

      {/* Non-invoiceable articles alert */}
      {nonInvoiceableLines.length > 0 && !keepNonInvoiceable && (
        <Card className="border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-yellow-600 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium">{t('sales.nonInvoiceableAlert.title')}</p>
                <p className="text-xs text-muted-foreground">{t('sales.nonInvoiceableAlert.description')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {nonInvoiceableLines.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-yellow-700">{name}</Badge>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => setKeepNonInvoiceable(true)}>
                  {t('sales.nonInvoiceableAlert.keepAll')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!isNew && document && <LinkedDocuments document={document} />}

      {/* Credit note — always use dedicated flow */}
      {isNew && docType === 'credit_note' ? (
        <CreditNoteForm
          sourceDocId={fromId || undefined}
          onSaved={() => navigate(afterSavePath)}
          onCancel={() => navigate(basePath)}
        />
      ) : (
      <Card>
        <CardContent className="pt-4 md:pt-6 px-3 md:px-4">
          <SalesDocumentForm
            key={`${initialDoc?.id || (fromId ? `new-from-${fromId}` : 'new')}-${keepNonInvoiceable}`}
            docType={docType}
            document={initialDoc}
            onSaved={() => navigate(afterSavePath)}
            onCancel={() => navigate(basePath)}
          />
        </CardContent>
      </Card>
      )}
    </div>
  );
}
